import React, { Component } from 'react';
import Modal from '../../components/Modal';
import PlotContent from './chaptersContainer/plotModal/PlotContent';
import PlotHolesSection from './chaptersContainer/plotModal/PlotHolesSection';
import StructuralThematicModal from './StructuralThematicModal';
import RewritePlanModal from './RewritePlanModal';
import TabsMenu from '../../../components/TabsMenu';
import { urlMap, getAuthHeaders } from '../../../utils';

const TABS = [
  { key: 'plot', label: 'Plot' },
  { key: 'summaries', label: 'Chapter Summaries' },
  { key: 'characters', label: 'Characters' },
  { key: 'structure', label: 'Structure & Themes' },
  { key: 'issues', label: 'Potential Issues' },
  { key: 'rewritePlan', label: 'Rewrite Plan' },
];

/**
 * PlotModal
 *
 * Unified modal with six tabs: Plot, Chapter Summaries, Characters,
 * Structure & Themes, Rewrite Plan, Issues.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - project: object
 *  - refreshTrigger: number
 *  - initialTab: string
 */
export default class PlotModal extends Component {
  state = {
    activeTab: this.props.initialTab || 'plot',
    summaries: [],
    characters: [],
    loading: false,
    selectedCharacterId: null,
    expandedHoles: {},
    savingManual: false,
    manualError: '',
    manualPlotThemes: '',
    manualPlotMainConflict: '',
    manualPlotResolution: '',
    manualPlotPoints: '',
    manualSummaryChapter: 1,
    manualSummaryText: '',
    manualCharacterName: '',
    manualCharacterRole: 'supporting',
    manualCharacterDescription: '',
    manualIssueTitle: '',
    manualIssueSeverity: 'medium',
    manualIssueDescription: '',
    manualStructureTitle: '',
    manualStructureScope: 'structural',
    manualStructureSeverity: 'medium',
    manualStructureSuggestion: '',
  };

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.setState({ activeTab: this.props.initialTab || 'plot', selectedCharacterId: null, expandedHoles: {} });
      this.fetchData();
    }
    if (
      this.props.isOpen &&
      prevProps.refreshTrigger !== this.props.refreshTrigger
    ) {
      this.fetchData();
    }
    if (!this.props.isOpen && prevProps.isOpen) {
      this.setState({ selectedCharacterId: null });
    }
  }

  fetchData = async () => {
    const projectId = this.props.project?.id;
    if (!projectId) return;
    this.setState({ loading: true });
    try {
      const [sumRes, charRes] = await Promise.all([
        fetch(urlMap.getProjectSummaries(projectId), { headers: getAuthHeaders() }),
        fetch(urlMap.getProjectCharactersWithAppearances(projectId), { headers: getAuthHeaders() }),
      ]);
      const summaries = sumRes.ok ? await sumRes.json() : [];
      const charBody = charRes.ok ? await charRes.json() : { characters: [] };
      this.setState({
        summaries: Array.isArray(summaries) ? summaries : [],
        characters: Array.isArray(charBody.characters) ? charBody.characters : [],
        loading: false,
      });
    } catch {
      this.setState({ loading: false });
    }
  };

  patchProjectMetadata = async (patch) => {
    const projectId = this.props.project?.id;
    if (!projectId) return false;
    this.setState({ savingManual: true, manualError: '' });
    try {
      const response = await fetch(`${urlMap.projectEndpoint}/${projectId}/metadata`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await this.props.onRefreshProject?.();
      this.setState({ savingManual: false });
      return true;
    } catch (err) {
      this.setState({ savingManual: false, manualError: err?.message || 'Failed to save changes.' });
      return false;
    }
  };

  parseManualPlotPoints = (raw) => {
    const lines = String(raw || '').split('\n').map((line) => line.trim()).filter(Boolean);
    return lines.map((line, idx) => {
      const chapterMatch = line.match(/ch(?:apter)?\s*(\d+)/i);
      const chapterNumber = chapterMatch ? Number(chapterMatch[1]) : (idx + 1);
      const parts = line.split(':');
      const head = (parts[0] || '').trim();
      const tail = (parts.slice(1).join(':') || line).trim();
      return {
        chapterNumber,
        plotElement: head || `Plot point ${idx + 1}`,
        significance: 'medium',
        description: tail,
      };
    });
  };

  buildPlotHolesPayload = (issues) => {
    const list = Array.isArray(issues) ? issues : [];
    const count = (severity) => list.filter((i) => String(i.severity || '').toLowerCase() === severity).length;
    return {
      plotHoles: list,
      totalIssues: list.length,
      criticalCount: count('critical'),
      highSeverityCount: count('high'),
      mediumSeverityCount: count('medium'),
      lowSeverityCount: count('low'),
    };
  };

  saveManualPlot = async () => {
    const themes = this.state.manualPlotThemes
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const plotPoints = this.parseManualPlotPoints(this.state.manualPlotPoints);
    const payload = {
      themes,
      mainConflict: this.state.manualPlotMainConflict.trim(),
      plotPoints,
      resolution: this.state.manualPlotResolution.trim(),
    };
    const saved = await this.patchProjectMetadata({ plot: payload });
    if (saved) {
      this.setState({
        manualPlotThemes: '',
        manualPlotMainConflict: '',
        manualPlotResolution: '',
        manualPlotPoints: '',
      });
    }
  };

  addManualSummary = async () => {
    const text = this.state.manualSummaryText.trim();
    if (!text) return;
    const chapterNo = Math.max(1, Number(this.state.manualSummaryChapter) || 1);
    const manualSummaries = Array.isArray(this.props.project?.metadata?.manualPlotSummaries)
      ? this.props.project.metadata.manualPlotSummaries
      : [];
    const next = [
      ...manualSummaries,
      {
        chapter_id: `manual-${Date.now()}`,
        order_no: chapterNo,
        summary: text,
      },
    ];
    const saved = await this.patchProjectMetadata({ manualPlotSummaries: next });
    if (saved) this.setState({ manualSummaryText: '' });
  };

  addManualCharacter = async () => {
    const name = this.state.manualCharacterName.trim();
    if (!name) return;
    const manualCharacters = Array.isArray(this.props.project?.metadata?.manualPlotCharacters)
      ? this.props.project.metadata.manualPlotCharacters
      : [];
    const next = [
      ...manualCharacters,
      {
        id: `manual-${Date.now()}`,
        name,
        role: this.state.manualCharacterRole,
        description: this.state.manualCharacterDescription.trim(),
      },
    ];
    const saved = await this.patchProjectMetadata({ manualPlotCharacters: next });
    if (saved) {
      this.setState({ manualCharacterName: '', manualCharacterDescription: '' });
    }
  };

  addManualIssue = async () => {
    const title = this.state.manualIssueTitle.trim();
    if (!title) return;
    const current = Array.isArray(this.props.project?.metadata?.plotHoles?.plotHoles)
      ? this.props.project.metadata.plotHoles.plotHoles
      : [];
    const next = [
      ...current,
      {
        title,
        severity: this.state.manualIssueSeverity,
        whyItsIssue: this.state.manualIssueDescription.trim(),
        suggestedFix: '',
      },
    ];
    const saved = await this.patchProjectMetadata({ plotHoles: this.buildPlotHolesPayload(next) });
    if (saved) {
      this.setState({ manualIssueTitle: '', manualIssueDescription: '' });
    }
  };

  addManualStructureIssue = async () => {
    const title = this.state.manualStructureTitle.trim();
    if (!title) return;
    const manualStructure = Array.isArray(this.props.project?.metadata?.manualStructuralIssues)
      ? this.props.project.metadata.manualStructuralIssues
      : [];
    const next = [
      ...manualStructure,
      {
        id: `manual-${Date.now()}`,
        title,
        scope: this.state.manualStructureScope,
        severity: this.state.manualStructureSeverity,
        suggestedChange: this.state.manualStructureSuggestion.trim(),
        status: 'pending',
      },
    ];
    const saved = await this.patchProjectMetadata({ manualStructuralIssues: next });
    if (saved) {
      this.setState({ manualStructureTitle: '', manualStructureSuggestion: '' });
    }
  };

  getRoleBadgeClass = (role) => {
    switch (role) {
      case 'main': return 'character-role-badge--main';
      case 'supporting': return 'character-role-badge--supporting';
      case 'minor': return 'character-role-badge--minor';
      default: return '';
    }
  };

  getRoleLabel = (role) => {
    switch (role) {
      case 'main': return 'Main';
      case 'supporting': return 'Supporting';
      case 'minor': return 'Minor';
      default: return role;
    }
  };

  render() {
    const { isOpen, onClose, project } = this.props;
    if (!isOpen) return null;

    const { activeTab, summaries, characters, loading, selectedCharacterId, expandedHoles } = this.state;
    const plotStructure = project?.metadata?.plot || null;
    const plotHoles = project?.metadata?.plotHoles || null;
    const manualSummaries = Array.isArray(project?.metadata?.manualPlotSummaries) ? project.metadata.manualPlotSummaries : [];
    const manualCharacters = Array.isArray(project?.metadata?.manualPlotCharacters) ? project.metadata.manualPlotCharacters : [];
    const manualStructureIssues = Array.isArray(project?.metadata?.manualStructuralIssues) ? project.metadata.manualStructuralIssues : [];
    const resolvedSummaries = summaries.length > 0 ? summaries : manualSummaries;
    const resolvedCharacters = characters.length > 0 ? characters : manualCharacters;
    const plotHolesCount = plotHoles?.totalIssues || 0;
    const structureIssues = project?.metadata?.structuralThematicIssues;
    const structureCount = Array.isArray(structureIssues) ? structureIssues.length : 0;
    const rewriteActions = project?.metadata?.rewritePlan?.actions;
    const rewriteCount = Array.isArray(rewriteActions) ? rewriteActions.length : 0;
    const tabs = TABS.map((tab) => {
      let badge = null;
      if (tab.key === 'summaries') badge = summaries.length;
      if (tab.key === 'summaries' && badge === 0) badge = manualSummaries.length;
      if (tab.key === 'characters') badge = characters.length;
      if (tab.key === 'characters' && badge === 0) badge = manualCharacters.length;
      if (tab.key === 'structure' && structureCount > 0) badge = structureCount;
      if (tab.key === 'structure' && badge === null && manualStructureIssues.length > 0) badge = manualStructureIssues.length;
      if (tab.key === 'rewritePlan' && rewriteCount > 0) badge = rewriteCount;
      if (tab.key === 'issues' && plotHolesCount > 0) badge = plotHolesCount;
      return {
        key: tab.key,
        label: tab.label,
        badge,
        badgeClassName: 'scm-tab-count',
      };
    });

    const sorted = [...resolvedCharacters].sort(
      (a, b) => (b.mention_count || 0) - (a.mention_count || 0)
    );

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="chars-modal-content">
          <div style={{ padding: '0 16px' }}>
            <TabsMenu
              className="scm-tabs"
              ariaLabel="Plot modal sections"
              activeKey={activeTab}
              onChange={(key) => this.setState({ activeTab: key })}
              tabs={tabs}
            />
          </div>

          <div className="chars-modal-body">
            {activeTab === 'plot' && (
              plotStructure ? (
                <PlotContent plotStructure={plotStructure} />
              ) : (
                <div className="scm-manual-wrap">
                  <p className="scm-empty">No plot structure available yet.</p>
                  <div className="scm-manual-form">
                    <label className="scm-manual-label">Themes (comma separated)</label>
                    <input
                      className="scm-manual-input"
                      value={this.state.manualPlotThemes}
                      onChange={(e) => this.setState({ manualPlotThemes: e.target.value })}
                      placeholder="Identity, Trust, Belonging"
                    />
                    <label className="scm-manual-label">Main conflict</label>
                    <textarea
                      className="scm-manual-textarea"
                      rows={3}
                      value={this.state.manualPlotMainConflict}
                      onChange={(e) => this.setState({ manualPlotMainConflict: e.target.value })}
                    />
                    <label className="scm-manual-label">Plot points (one per line)</label>
                    <textarea
                      className="scm-manual-textarea"
                      rows={4}
                      value={this.state.manualPlotPoints}
                      onChange={(e) => this.setState({ manualPlotPoints: e.target.value })}
                      placeholder="Ch 1: Inciting incident\nCh 5: Midpoint reversal"
                    />
                    <label className="scm-manual-label">Resolution</label>
                    <textarea
                      className="scm-manual-textarea"
                      rows={3}
                      value={this.state.manualPlotResolution}
                      onChange={(e) => this.setState({ manualPlotResolution: e.target.value })}
                    />
                    <button type="button" className="btn primary" onClick={this.saveManualPlot} disabled={this.state.savingManual}>
                      {this.state.savingManual ? 'Saving…' : 'Save Plot by Hand'}
                    </button>
                  </div>
                </div>
              )
            )}

            {activeTab === 'summaries' && (
              <>
                {loading && <p className="scm-empty">Loading…</p>}
                {!loading && (
                  <div className="scm-summaries-list">
                    {resolvedSummaries.length === 0 && <p className="scm-empty">No summaries yet.</p>}
                    {resolvedSummaries.map((s, i) => (
                      <div key={s.chapter_id || i} className="scm-summary-card">
                        <h4 className="scm-summary-title">Chapter {s.order_no || i + 1}</h4>
                        <p className="scm-summary-text">{s.summary}</p>
                      </div>
                    ))}
                    <div className="scm-manual-form scm-manual-form--inline">
                      <label className="scm-manual-label">Add summary manually</label>
                      <div className="scm-manual-row">
                        <input
                          type="number"
                          min={1}
                          className="scm-manual-input scm-manual-input--small"
                          value={this.state.manualSummaryChapter}
                          onChange={(e) => this.setState({ manualSummaryChapter: Number(e.target.value) || 1 })}
                        />
                        <textarea
                          className="scm-manual-textarea"
                          rows={3}
                          value={this.state.manualSummaryText}
                          onChange={(e) => this.setState({ manualSummaryText: e.target.value })}
                          placeholder="Write chapter summary..."
                        />
                      </div>
                      <button type="button" className="btn primary" onClick={this.addManualSummary} disabled={this.state.savingManual}>
                        {this.state.savingManual ? 'Saving…' : 'Add Summary'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'characters' && (
              <>
                {loading && <p className="scm-empty">Loading…</p>}
                {!loading && (
                  <>
                    {sorted.length === 0 && <p className="scm-empty">No characters extracted yet.</p>}
                    {sorted.length > 0 && (
                      <div className="chars-gallery">
                        {sorted.map((c) => {
                          const isOpenCard = selectedCharacterId === (c.id || c.name);
                          return (
                            <div
                              key={c.id || c.name}
                              className={`chars-tile-wrapper${isOpenCard ? ' chars-tile--flipped' : ''}`}
                              onClick={() =>
                                this.setState((s) => ({
                                  selectedCharacterId:
                                    s.selectedCharacterId === (c.id || c.name) ? null : (c.id || c.name),
                                }))
                              }
                            >
                              <div className="chars-tile-inner">
                                {/* Front */}
                                <div className="chars-tile-front">
                                  <div className={`chars-tile-avatar ${this.getRoleBadgeClass(c.role)}`}>
                                    <span className="chars-tile-avatar-name">{c.name}</span>
                                  </div>
                                  <div className="chars-tile-footer">
                                    <span className={`chars-tile-role ${this.getRoleBadgeClass(c.role)}`}>
                                      {this.getRoleLabel(c.role)}
                                    </span>
                                    <span className="chars-tile-flip-icon">↻</span>
                                  </div>
                                </div>
                                {/* Back */}
                                <div className="chars-tile-back">
                                  <span className="chars-tile-back-name">{c.name}</span>
                                  <div className="chars-tile-back-body">
                                    {c.description && <p className="chars-tile-desc">{c.description}</p>}
                                    {c.aliases?.length > 0 && (
                                      <div className="chars-tile-row">
                                        <span className="chars-tile-label">Aliases:</span> {c.aliases.join(', ')}
                                      </div>
                                    )}
                                    {c.first_appearance && (
                                      <div className="chars-tile-row">
                                        <span className="chars-tile-label">First:</span> Ch {c.first_appearance}
                                      </div>
                                    )}
                                    {(c.chapter_appearances?.length > 0 || c.appearances?.length > 0) && (
                                      <div className="chars-tile-row">
                                        <span className="chars-tile-label">Appears in:</span>{' '}
                                        {(c.chapter_appearances || c.appearances).length} chapter
                                        {(c.chapter_appearances || c.appearances).length !== 1 ? 's' : ''}
                                      </div>
                                    )}
                                  </div>
                                  <span className="chars-tile-flip-icon">↺</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="scm-manual-form scm-manual-form--inline">
                      <label className="scm-manual-label">Add character manually</label>
                      <input
                        className="scm-manual-input"
                        value={this.state.manualCharacterName}
                        onChange={(e) => this.setState({ manualCharacterName: e.target.value })}
                        placeholder="Character name"
                      />
                      <div className="scm-manual-row">
                        <select
                          className="scm-manual-select"
                          value={this.state.manualCharacterRole}
                          onChange={(e) => this.setState({ manualCharacterRole: e.target.value })}
                        >
                          <option value="main">Main</option>
                          <option value="supporting">Supporting</option>
                          <option value="minor">Minor</option>
                        </select>
                        <textarea
                          className="scm-manual-textarea"
                          rows={3}
                          value={this.state.manualCharacterDescription}
                          onChange={(e) => this.setState({ manualCharacterDescription: e.target.value })}
                          placeholder="Role, motivation, and core trait"
                        />
                      </div>
                      <button type="button" className="btn primary" onClick={this.addManualCharacter} disabled={this.state.savingManual}>
                        {this.state.savingManual ? 'Saving…' : 'Add Character'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {activeTab === 'issues' && (
              <>
                {plotHoles?.plotHoles?.length ? (
                  <PlotHolesSection
                    plotHoles={plotHoles}
                    expandedHoles={expandedHoles}
                    onToggleHole={(index) =>
                      this.setState((s) => ({
                        expandedHoles: { ...s.expandedHoles, [index]: !s.expandedHoles[index] },
                      }))
                    }
                    title="Potential Issues"
                  />
                ) : (
                  <p className="scm-empty">No potential issues yet.</p>
                )}
                <div className="scm-manual-form scm-manual-form--inline">
                  <label className="scm-manual-label">Add potential issue manually</label>
                  <input
                    className="scm-manual-input"
                    value={this.state.manualIssueTitle}
                    onChange={(e) => this.setState({ manualIssueTitle: e.target.value })}
                    placeholder="Issue title"
                  />
                  <div className="scm-manual-row">
                    <select
                      className="scm-manual-select"
                      value={this.state.manualIssueSeverity}
                      onChange={(e) => this.setState({ manualIssueSeverity: e.target.value })}
                    >
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                    <textarea
                      className="scm-manual-textarea"
                      rows={3}
                      value={this.state.manualIssueDescription}
                      onChange={(e) => this.setState({ manualIssueDescription: e.target.value })}
                      placeholder="Why this is an issue"
                    />
                  </div>
                  <button type="button" className="btn primary" onClick={this.addManualIssue} disabled={this.state.savingManual}>
                    {this.state.savingManual ? 'Saving…' : 'Add Issue'}
                  </button>
                </div>
              </>
            )}

            {activeTab === 'structure' && (
              structureCount > 0 ? (
                <StructuralThematicModal
                  isOpen
                  embedded
                  onClose={onClose}
                  project={project}
                  onRefreshProject={this.props.onRefreshProject}
                  onIssuesUpdated={this.props.onStructuralIssuesUpdated}
                  onIssuesCountChanged={this.props.onIssuesCountChanged}
                />
              ) : (
                <div className="scm-manual-wrap">
                  <p className="scm-empty">No structural or thematic changes proposed.</p>
                  {manualStructureIssues.length > 0 && (
                    <div className="scm-summaries-list">
                      {manualStructureIssues.map((issue, idx) => (
                        <div key={issue.id || idx} className="scm-summary-card">
                          <h4 className="scm-summary-title">{issue.title}</h4>
                          <p className="scm-summary-text">
                            {(issue.scope || 'structural')} • {(issue.severity || 'medium')}
                          </p>
                          {issue.suggestedChange ? (
                            <p className="scm-summary-text">{issue.suggestedChange}</p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="scm-manual-form scm-manual-form--inline">
                    <label className="scm-manual-label">Add structural/thematic change manually</label>
                    <input
                      className="scm-manual-input"
                      value={this.state.manualStructureTitle}
                      onChange={(e) => this.setState({ manualStructureTitle: e.target.value })}
                      placeholder="Change title"
                    />
                    <div className="scm-manual-row">
                      <select
                        className="scm-manual-select"
                        value={this.state.manualStructureScope}
                        onChange={(e) => this.setState({ manualStructureScope: e.target.value })}
                      >
                        <option value="structural">Structural</option>
                        <option value="thematic">Thematic</option>
                      </select>
                      <select
                        className="scm-manual-select"
                        value={this.state.manualStructureSeverity}
                        onChange={(e) => this.setState({ manualStructureSeverity: e.target.value })}
                      >
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                    <textarea
                      className="scm-manual-textarea"
                      rows={3}
                      value={this.state.manualStructureSuggestion}
                      onChange={(e) => this.setState({ manualStructureSuggestion: e.target.value })}
                      placeholder="Suggested change"
                    />
                    <button type="button" className="btn primary" onClick={this.addManualStructureIssue} disabled={this.state.savingManual}>
                      {this.state.savingManual ? 'Saving…' : 'Add Change'}
                    </button>
                  </div>
                </div>
              )
            )}

            {activeTab === 'rewritePlan' && (
              <RewritePlanModal
                isOpen
                embedded
                onClose={onClose}
                project={project}
                onRefreshProject={this.props.onRefreshProject}
                onTriggerAgentStep={this.props.onTriggerAgentStep}
              />
            )}
          </div>
          {this.state.manualError ? <p className="scm-manual-error">{this.state.manualError}</p> : null}
        </div>
      </Modal>
    );
  }
}
